// Based mostly on https://github.com/Uniswap/interface/blob/main/src/theme/index.tsx
import { opacify } from './utils'

export const colors = {
  accent: '#FF3E36',
  neutralDark: '#1A0F15',
  neutralBase: '#121212',
  neutralMid: '#2C0D1DE5',
  neutralLight: '#3B2943',
  neutralLighter: '#949494',
  neutralLightest: '#423249',
  light: '#E0E0E0',
  primaryDark: '#99C74B',
  primaryBase: '#B4EB56',
  primaryMid: '#DE9E44',
  primaryLight: '#B4EB56',
  positiveDark: '#16A249',
  positiveBase: '#22C55E',
  positiveMid: '#4ADE80',
  positiveLight: '#85EFAC',
  negativeDark: '#C73D3D',
  negativeBase: '#EF4444',
  negativeMid: '#F26666',
  negativeLight: '#FDA4AF',
  white: '#FFFFFF',
  black: '#000000',
  aquaBlue: '#00FFFF',
  brightBlue: '#15CEF7',
  gray: '#6071AE',
  poorBlue: '#81A1E1',
  violet: '#A589FF',
  royalBlue: '#5B53D8',
  royalBlue10: 'rgba(91, 83, 216, 0.1)',
  deepPurple: '#291860',
  lowBlue: '#1F357D',
  deepBlue: '#142970',
  deepBlue50: 'rgba(20, 41, 112, 0.5)',
  darkBlue: '#132562',
  downRiver: '#0C1A4A',
  deepCove: '#05113B',
  deepCove42: 'rgba(5, 17, 59, 0.42)',
  deepCove80: 'rgba(5, 17, 59, 0.8)',
  gray50: '#F5F6FC',
  gray100: '#E8ECFB',
  gray150: '#D2D9EE',
  gray200: '#B7B8BA',
  gray250: '#B7B8BA',
  gray300: '#B7B8BA',
  gray350: '#B7B8BA',
  gray400: '#957C89',
  gray450: '#606060',
  gray500: '#606060',
  gray550: '#4B484D',
  gray600: '#272727',
  gray650: '#272727',
  gray700: '#272727',
  gray750: '#272727',
  gray800: '#272727',
  gray850: '#202020',
  gray900: '#121212',
  gray950: '#0B0C0C',
  pink50: '#F9ECF1',
  pink100: '#FFD9E4',
  pink200: '#FBA4C0',
  pink300: '#FF6FA3',
  pink400: '#FB118E',
  pink500: '#C41969',
  pink600: '#8C0F49',
  pink700: '#55072A',
  pink800: '#350318',
  pink900: '#2B000B',
  pinkVibrant: '#F51A70',
  red50: '#FAECEA',
  red100: '#FED5CF',
  red200: '#FEA79B',
  red300: '#FD766B',
  red400: '#FA2B39',
  red500: '#B82921',
  red600: '#891E20',
  red700: '#530F0F',
  red800: '#380A03',
  red900: '#240800',
  redVibrant: '#F14544',
  yellow50: '#F6F2D5',
  yellow100: '#DBBC19',
  yellow200: '#DBBC19',
  yellow300: '#BB9F13',
  yellow400: '#A08116',
  yellow500: '#866311',
  yellow600: '#5D4204',
  yellow700: '#3E2B04',
  yellow800: '#231902',
  yellow900: '#180F02',
  yellowVibrant: '#FAF40A',
  gold200: '#EEB317',
  gold400: '#B17900',
  goldVibrant: '#FEB239',
  green50: '#E3F3E6',
  green100: '#BFEECA',
  green200: '#76D191',
  green300: '#40B66B',
  green400: '#209853',
  green500: '#0B783E',
  green600: '#0C522A',
  green700: '#053117',
  green800: '#091F10',
  green900: '#09130B',
  greenVibrant: '#5CFE9D',
  blue50: '#EDEFF8',
  blue100: '#DEE1FF',
  blue200: '#ADBCFF',
  blue300: '#869EFF',
  blue400: '#4C82FB',
  blue500: '#1267D6',
  blue600: '#1D4294',
  blue700: '#09265E',
  blue800: '#0B193F',
  blue900: '#040E34',
  blueVibrant: '#587BFF',
  magenta300: '#FD82FF',
  magentaVibrant: '#FC72FF',
  purple300: '#8440F2',
  purple900: '#1C0337',
  networkEthereum: '#627EEA',
  networkOptimism: '#FF0420',
  networkOptimismSoft: 'rgba(255, 4, 32, 0.16)',
  networkPolygon: '#A457FF',
  networkArbitrum: '#28A0F0',
  networkBsc: '#F0B90B',
  networkPolygonSoft: 'rgba(164, 87, 255, 0.16)',
  networkEthereumSoft: 'rgba(98, 126, 234, 0.16)',
  rubyBase: '#591429',
  ruby: '#4F1520',
  rubyDark: '#4c1022',
  rubyPrimary: '#814464',
  rubySecondary: '#5D2D46',
}

type Theme = typeof darkTheme

export const gradients = {
  brandedGradient: 'linear-gradient(270deg, #AB2B22 0%, #E13E1D 100%)',
  promotionalGradient: 'linear-gradient(270deg, #D0372C 0%, #FF6141 100%)',
  fillGradient: 'linear-gradient(180deg, rgba(19, 37, 98, 0) 0%, #132562 100%)',
  outlineGradientPrimary: 'linear-gradient(135.27deg, #4866CB -17.16%, rgba(72, 102, 203, 0) 71.02%)',
  outlineGradientSecondary: 'linear-gradient(152.69deg, #FFFFFF -16.22%, rgba(255, 255, 255, 0) 76.37%)',
  // duc
  mainGradientPrimary: 'linear-gradient(270deg, #AB2B22 0%, #E13E1D 100%)',
  mainGradientSecondary: 'linear-gradient(270deg, #D0372C 0%, #FF6141 100%)',
  mainGradientTertiary: 'linear-gradient(90deg, #25162B 0%, #461B2F 100%)',

  surfaceDefaultBackground: 'linear-gradient(180deg, rgba(44, 13, 29, 0.8) 0%, rgba(33, 9, 22, 0.8) 100%)',
  surfaceClearBackground: 'linear-gradient(98.58deg, rgba(62, 41, 70, 0.6) -1.7%, rgba(36, 19, 40, 0.4) 56.55%)',

  gradientBorderPrimary: 'linear-gradient(180deg, #814464 0%, rgba(44, 13, 29, 0) 100%)',
  gradientBorderSecondary: 'linear-gradient(171.1deg, #814464 58.39%, rgba(44, 13, 29, 0) 133.63%)',
  gradientBorderTertiary: 'linear-gradient(98.58deg, rgba(62, 41, 70, 0.6) -1.7%, rgba(36, 19, 40, 0.4) 56.55%)',
  gradientDividerPrimary:
    'linear-gradient(90deg, rgba(91, 45, 81, 0) 0%, rgba(94, 35, 68, 1) 50.48%, rgba(113, 38, 80, 1) 100%)',
  gradientDividerSecondary:
    'linear-gradient(180deg, rgba(91, 45, 81, 0) 0%, rgba(94, 35, 68, 1) 50.48%, rgba(113, 38, 80, 1) 100%)',
  gradientDividerBlurPrimary:
    'linear-gradient(90deg, rgba(222, 75, 68, 0) 0%, #DE4B44 48.56%, rgba(222, 75, 68, 0) 100%)',
  gradientDividerBlurSecondary:
    'linear-gradient(180deg, rgba(222, 75, 68, 0) 0%, #DE4B44 48.56%, rgba(222, 75, 68, 0) 100%)',
}

const commonTheme = {
  white: colors.white,
  black: colors.black,
  promotional: colors.magenta300,

  ...gradients,

  borderPrimary: colors.rubyPrimary,
  borderPrimaryHover: colors.neutralLight,
  borderSecondary: colors.gray400,
  borderTertiary: '#735F7C',
  borderAccent: colors.red500,

  buttonPrimary: gradients.mainGradientPrimary,
  buttonPrimaryHover: gradients.mainGradientSecondary,
  buttonPrimaryText: colors.white,
  buttonSecondary: colors.neutralLightest,
  buttonSecondaryHover: colors.neutralLight,
  buttonSecondaryText: colors.neutralLightest,
  buttonDisabled: colors.ruby,
  buttonTextDisabled: '#957C8980',

  selectPrimary: colors.rubyBase,
  selectPrimaryHover: colors.rubyDark,

  hoverState: opacify(24, colors.primaryLight),
  hoverDefault: opacify(8, colors.neutralLight),
}

export const darkTheme = {
  ...commonTheme,

  userThemeColor: colors.magentaVibrant,

  background: colors.neutralDark,
  backgroundBackdrop: colors.neutralBase,
  backgroundSurface: opacify(61, colors.neutralBase),
  backgroundModule: colors.neutralMid,
  backgroundInteractive: colors.neutralLight,
  backgroundInput: gradients.mainGradientTertiary,
  backgroundFloating: opacify(12, colors.neutralBase),
  backgroundOutline: opacify(24, colors.neutralBase),
  backgroundScrim: opacify(72, colors.neutralBase),
  backgroundScrolledSurface: opacify(72, colors.neutralBase),

  iconPrimary: colors.primaryBase,

  textPrimary: colors.white,
  textSecondary: colors.gray400,
  textTertiary: colors.neutralLighter,

  accentAction: colors.primaryMid,
  accentActive: colors.primaryMid,
  accentSuccess: colors.positiveBase,
  accentWarning: colors.primaryDark,
  accentFailure: colors.negativeMid,
  accentCritical: colors.negativeBase,

  accentActionSoft: opacify(24, colors.primaryMid),
  accentActiveSoft: opacify(24, colors.primaryMid),
  accentSuccessSoft: opacify(24, colors.positiveBase),
  accentWarningSoft: opacify(24, colors.primaryDark),
  accentFailureSoft: opacify(12, colors.negativeMid),

  accentTextDarkPrimary: opacify(80, colors.primaryDark),
  accentTextDarkSecondary: opacify(60, colors.primaryDark),
  accentTextDarkTertiary: opacify(24, colors.primaryDark),

  accentTextLightPrimary: colors.neutralLight,
  accentTextLightSecondary: opacify(72, colors.neutralLight),
  accentTextLightTertiary: opacify(12, colors.neutralLight),

  defaultShadow: '0px 4px 4px 0px #00000040',
  deepShadow: '12px 16px 24px rgba(0, 0, 0, 0.24), 12px 8px 12px rgba(0, 0, 0, 0.24), 4px 4px 8px rgba(0, 0, 0, 0.32);',
  shallowShadow: '4px 4px 10px rgba(0, 0, 0, 0.24), 2px 2px 4px rgba(0, 0, 0, 0.12), 1px 2px 2px rgba(0, 0, 0, 0.12);',

  networkDefaultShadow: `0px 40px 120px ${opacify(16, colors.primaryBase)}`,

  stateOverlayHover: opacify(8, colors.gray300),
  stateOverlayPressed: opacify(24, colors.gray200),

  searchBackground: colors.neutralDark,
  searchOutline: colors.gray850,
  searchOutlineActive: colors.gray550,
}

export const lightTheme: Theme = {
  ...commonTheme,

  userThemeColor: colors.magentaVibrant,

  background: '#faf9fa', //INTENTIONALLY OFF THEME TO GIVE WHITE BG A SOFTER VISUAL
  backgroundBackdrop: colors.white,
  backgroundSurface: colors.white,
  backgroundModule: colors.gray50,
  backgroundInteractive: colors.gray100,
  backgroundInput: gradients.mainGradientTertiary,
  backgroundFloating: opacify(8, colors.gray700),
  backgroundOutline: colors.gray150,
  backgroundScrim: opacify(60, colors.gray900),
  backgroundScrolledSurface: opacify(72, colors.white),

  iconPrimary: colors.primaryMid,

  textPrimary: colors.gray900,
  textSecondary: colors.gray400,
  textTertiary: colors.gray300,

  accentAction: colors.pink400,
  accentActive: colors.primaryMid,
  accentSuccess: colors.green300,
  accentWarning: colors.gold400,
  accentFailure: colors.red400,
  accentCritical: colors.red400,

  accentActionSoft: opacify(12, colors.pink400),
  accentActiveSoft: opacify(24, colors.primaryMid),
  accentSuccessSoft: opacify(24, colors.green300),
  accentWarningSoft: opacify(24, colors.gold400),
  accentFailureSoft: opacify(12, colors.red400),

  accentTextDarkPrimary: opacify(80, colors.gray900),
  accentTextDarkSecondary: opacify(60, colors.gray900),
  accentTextDarkTertiary: opacify(24, colors.gray900),

  accentTextLightPrimary: colors.gray50,
  accentTextLightSecondary: opacify(72, colors.gray50),
  accentTextLightTertiary: opacify(12, colors.gray50),

  defaultShadow: '0px 4px 4px 0px #00000040',
  deepShadow:
    '8px 12px 20px rgba(51, 53, 72, 0.04), 4px 6px 12px rgba(51, 53, 72, 0.02), 4px 4px 8px rgba(51, 53, 72, 0.04);',
  shallowShadow:
    '6px 6px 10px rgba(51, 53, 72, 0.01), 2px 2px 6px rgba(51, 53, 72, 0.02), 1px 2px 2px rgba(51, 53, 72, 0.02);',

  networkDefaultShadow: `0px 40px 120px ${opacify(12, colors.pink400)}`,

  stateOverlayHover: opacify(8, colors.gray300),
  stateOverlayPressed: opacify(24, colors.gray200),

  searchBackground: opacify(4, colors.white),
  searchOutline: opacify(1, colors.black),
  searchOutlineActive: colors.gray550,
}
